// src/components/phase05/vitals/VitalsMonitorPrint.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchVitalsChart } from "../../../store/slices/vitalsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function VitalsMonitorPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentVitalsChart, isLoading, error } = useSelector((state) => state.vitals || {});

  useEffect(() => {
    if (id) dispatch(fetchVitalsChart(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentVitalsChart) {
      const timer = setTimeout(() => window.print(), 600);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentVitalsChart, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentVitalsChart) return <div className="p-8 text-center">No record found.</div>;

  const d = currentVitalsChart;

  // FIXED: Correct array name from API
  const rows = [...(d.vital_records || [])].sort((a, b) => {
    const aTime = a.date_time ? new Date(a.date_time).getTime() : (a.created_at ? new Date(a.created_at).getTime() : 0);
    const bTime = b.date_time ? new Date(b.date_time).getTime() : (b.created_at ? new Date(b.created_at).getTime() : 0);
    return bTime - aTime;
  });

  // Extract date & time from date_time field
  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    return new Date(dateTimeStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    return new Date(dateTimeStr).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return " ";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  return (
    <>
      <style jsx global>{`
        * { box-sizing: border-box !important; }
        body { font-family: Arial, sans-serif !important; background: #fff !important; color: #000 !important; margin: 0 !important; padding: 0 !important; }

        .print-container { padding: 20px !important; position: relative !important; }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }
        .logo img { height: 60px !important; width: auto !important; }

        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 14px !important;
          font-size: 1rem !important;
        }
        
        table.vitals-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 15px !important;
          font-size: 0.9em !important;
        }

        table.vitals-table th,
        table.vitals-table td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          text-align: center !important;
          vertical-align: middle !important;
        }

        table.vitals-table th {
          background-color: #f8f9fa !important;
          font-weight: bold !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1.5px solid #000 !important;
          padding-top: 8px !important;
          font-size: 1em !important;
        }

        .footer p {
          text-align: center !important;
          margin-bottom: 6px !important;
        }

        .footer-meta, .footer-sign {
          display: flex !important;
          justify-content: space-between !important;
          margin-top: 12px !important;
          font-size: 0.9em !important;
        }

        .underline {
          display: inline-block !important;
          width: auto !important;
          border-bottom: 1px solid #000 !important;
          margin-left: 5px !important;
        }

        @media print {
          .print-container { padding: 15px !important; }
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo"><img src={logo} alt="The Recovery House" /></div>
          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>Vitals Monitoring Chart</h6>
          </div>

        <div style={{ marginTop: "25px" }}>
          <div className="info-row">
            <div>
              <span>Client Name:</span>
              <span className="underline fw-bold">{d.name || " "}</span>
            </div>
            <div>
              <span>Age:</span>
              <span className="underline fw-bold">{calculateAge(d.dob || " ")}</span>
            </div>
          </div>
        </div>

        {/* Vitals Table - NOW 100% WORKING */}
        <table className="vitals-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>BP</th>
              <th>Pulse</th>
              <th>Resp.</th>
              <th>Temp.</th>
              <th>RBS</th>
              <th>FBS</th>
              <th>Weight</th>
              <th>Remarks</th>
              <th>Nurse Name</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.date_time)}</td>
                <td>{formatTime(row.date_time)}</td>
                <td>{row.bp || ""}</td>
                <td>{row.pulse || ""}</td>
                <td>{row.resp || ""}</td>
                <td>{row.temp || ""}</td>
                <td>{row.rbs || ""}</td>
                <td>{row.fbs || ""}</td>
                <td>{row.weight || ""}</td>
                <td style={{ textAlign: "left", paddingLeft: "10px" }}>{row.remarks || ""}</td>
                <td>{row.nurse || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div className="footer">
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}